'use client';

import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import Image from 'next/image';

import {
  ArrowUp,
  Bell,
  Bot,
  Check,
  CircleCheck,
  FileText,
  Frown,
  Hand,
  LockKeyhole,
  MessageSquare,
  Monitor,
  RotateCcw,
  Send,
  Signal,
  Smile,
  ThumbsDown,
  ThumbsUp,
  TriangleAlert,
  UserRound,
  Volume2,
  X,
  Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { AppLogo } from '@kit/ui/patterns/app-logo';

import styles from './website-hero-demo.module.css';

const LENGTH = 140000;
const clamp = (n: number) => Math.max(0, Math.min(1, n));
/** Local presentation only: no product handlers, network calls or account data. */
export function WebsiteHeroDemo({
  previewTime,
  entrance = 'slide',
}: { previewTime?: number; entrance?: 'slide' | 'cliff' } = {}) {
  const { t } = useTranslation('marketing');
  const cardCopy: Record<string, string> = {
    returned: 'Conversation handed back to Sarah',
    handback: 'Hand back to Sarah',
    agent: 'Sarah',
    role: 'Sales Assistant',
    summary:
      'Synthetic invoice-help demonstration: Sarah greets a customer, verifies their email with a one-time code, and helps with an invoice. Your team follows and guides the conversation in Monitor. No messages or payments are sent.',
    greeting:
      "Hi! My name is Sarah, I'm your friendly Sales Assistant from Leagen. How can I help you today?",
    question: 'I need help paying my invoice.',
    askIdentity:
      'Sure, I can help with that. Let’s start with your name and email.',
    identityReply: 'Alex Morgan — alex.morgan@example.com',
    otpPrompt:
      'I’ve sent a one-time password to your email. Enter it here to verify yourself.',
    otp: '482916',
    verified:
      'Thanks, Alex. Your email is verified. I’ll look up your invoice now.',
    actionName: 'Invoice lookup',
    actionLoading: 'Looking up your invoice…',
    actionDone: 'Invoice retrieved',
    actionLabel: 'Action',
    actionTitle: 'Connected to your systems',
    actionBody:
      'After email verification, Sarah uses a connected Action to find the customer’s invoice.',
    datesIntro: 'I found your outstanding invoice:',
    datesList: 'Invoice INV-1042 · $240.00',
    datesPrompt: 'Would you like a secure payment link?',
    instruction: 'Offer Alex a secure payment link for this invoice.',
    guided:
      'Alex, I can send a secure payment link so you can pay your invoice online.',
    followup:
      'This invoice charges me twice for the same service. I’ve already asked about this — I’m really frustrated.',
    reason: 'Duplicate charge · negative sentiment · billing team notified',
    reply:
      'Hi Alex, I’m Jordan from billing. You’re right — there is a duplicate $120 charge. I’ve removed it and corrected your invoice to $120. I’m sorry about that.',
    visitor: 'Alex Morgan',
    preview: 'I need help paying my invoice.',
    elapsed: '2m',
    messageCount: '8 msgs',
    unhappy: 'I’ve been charged twice. I’m really frustrated.',
    sentimentTitle: 'Your team can step in',
    sentimentBody:
      'The conversation moves to Needs Attention for a team member to help.',
  };
  const text = (key: string) =>
    cardCopy[key] ?? t(`heroDemo.${key}`, { defaultValue: key });
  const inputText = useRef<HTMLSpanElement>(null);
  const otpSuggestion = useRef<HTMLDivElement>(null);
  const liftedPanel = useRef<HTMLDivElement>(null);
  const [instructionPlane, setInstructionPlane] = useState({
    width: 430,
    height: 80,
    corners: [
      { x: 180, y: 490 },
      { x: 610, y: 490 },
      { x: 610, y: 570 },
      { x: 180, y: 570 },
    ],
  });
  const messageList = useRef<HTMLDivElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const target = useRef<HTMLDivElement>(null);
  const elapsed = useRef(0);
  const [clock, setTime] = useState(0);
  const time = previewTime ?? clock;
  const [scale, setScale] = useState(1);
  const [mobileViewport, setMobileViewport] = useState(false);
  const [desktopTop, setDesktopTop] = useState(150);
  const paused = false;
  const [reduced, setReduced] = useState(entrance !== 'cliff');
  const [, setPoint] = useState({ x: 330, y: 380 });
  const [cursorPoint, setCursorPoint] = useState({ x: 330, y: 380 });
  const [running, setRunning] = useState(false);
  const [arrived, setArrived] = useState(false);
  useEffect(() => {
    if (entrance === 'cliff' && !arrived && time >= 4200) setArrived(true);
  }, [arrived, entrance, time]);
  const scene = time < 48000 ? 0 : time < 64000 ? 1 : time < 80000 ? 2 : 3;
  const beat = time - [0, 48000, 64000, 80000][scene]!;
  const chatBeat = beat;
  const escalated = scene === 3 && beat >= 16000 && beat < 46000;
  const explainingEscalation =
    scene === 3 && beat >= 2800 && beat < 39000 && beat < 16000;
  const focusCard = scene === 3 && beat < 16000;
  const showingAction = scene === 0 && beat >= 33000;
  const actionComplete = scene > 0 || beat >= 35500;
  const desktop = scene > 0;
  const liftProgress =
    scene === 2
      ? Math.min(clamp((beat - 500) / 750), clamp((7000 - beat) / 750))
      : 0;
  const lift = liftProgress * liftProgress * (3 - 2 * liftProgress);
  const otpOffered = scene === 0 && beat >= 19000 && beat < 27500;
  const otpTapped = otpOffered && beat >= 26900;
  const staticTime = 138000;
  const eventWindows = [
    {
      key: 'customer',
      start: 4300,
      end: 11000,
      selector: '[data-audience]',
      title: 'Customer experience',
      body: 'This is how your customers experience Sarah on their phone: a conversation that helps them get things done.',
    },
    {
      key: 'otp',
      start: 19500,
      end: 26700,
      selector: '[data-otp]',
      title: 'Verify, then continue',
      body: 'A one-time code confirms Alex’s identity before Sarah retrieves personal invoice information. One tap fills the code.',
    },
    {
      key: 'action',
      start: 33200,
      end: 42000,
      selector: '[data-course-action]',
      title: 'A conversation becomes an Action',
      body: 'Connect your agent to third-party systems. Here, an invoice lookup Action retrieves Alex’s invoice from the billing system.',
    },
    {
      key: 'manager',
      start: 50200,
      end: 58000,
      selector: '[data-audience]',
      title: 'Team conversation manager',
      body: 'This is your monitoring and control panel. Follow live conversations, guide Sarah privately and step in when your team is needed.',
    },
    {
      key: 'sentiment',
      start: 80500,
      end: 95500,
      selector: '[data-live-card]',
      title: 'Your team can step in',
      body: 'AI detects Alex’s frustration: the smile becomes a sad face. Sentiment, requests for a person and other configured rules can trigger escalation. When a rule is met, the conversation moves to Needs attention and the team is notified.',
    },
    {
      key: 'handback',
      start: 126500,
      end: 133500,
      selector: '[data-handback]',
      title: 'Back to Sarah, with the context',
      body: 'Jordan has corrected the duplicate charge. The team hands the conversation back to Sarah, who continues with the corrected invoice and its payment link.',
    },
  ];
  const event = eventWindows.find(
    (item) => time >= item.start && time < item.end,
  );
  const eventProgress = event
    ? Math.min(
        clamp((time - event.start) / 900),
        clamp((event.end - time) / 900),
      )
    : 0;
  const eventLift = eventProgress * eventProgress * (3 - 2 * eventProgress);
  const [eventOrigin, setEventOrigin] = useState({
    width: 300,
    height: 100,
    corners: [
      { x: 160, y: 350 },
      { x: 460, y: 350 },
      { x: 460, y: 450 },
      { x: 160, y: 450 },
    ],
  });
  const eventSelector = event?.selector;
  useLayoutEffect(() => {
    if (!eventSelector || !stage.current) return;
    const source = stage.current.querySelector<HTMLElement>(eventSelector);
    if (!source) return;
    setEventOrigin(measurePlane(source, stage.current));
  }, [eventSelector, scale, time]);
  const foregroundScale = Math.min(2.5, 540 / eventOrigin.width);
  const foregroundWidth = eventOrigin.width * foregroundScale;
  const foregroundHeight = eventOrigin.height * foregroundScale;
  const foregroundLeft = (660 - foregroundWidth) / 2;
  const foregroundTop = desktop ? desktopTop + 100 : 230;
  const targetCorners = [
    { x: foregroundLeft, y: foregroundTop },
    { x: foregroundLeft + foregroundWidth, y: foregroundTop },
    {
      x: foregroundLeft + foregroundWidth,
      y: foregroundTop + foregroundHeight,
    },
    { x: foregroundLeft, y: foregroundTop + foregroundHeight },
  ];
  const liftedCorners = eventOrigin.corners.map((point, index) => ({
    x: point.x * (1 - eventLift) + targetCorners[index]!.x * eventLift,
    y: point.y * (1 - eventLift) + targetCorners[index]!.y * eventLift,
  }));
  const renderSentiment = (foreground = false) => (
    <div
      data-live-card
      style={{
        visibility:
          !foreground && event?.key === 'sentiment' ? 'hidden' : undefined,
      }}
      className={styles.liveCard}
      data-sad={scene === 3 && beat >= 2800 && beat < 39000}
      data-escalated={escalated}
    >
      <div className={styles.visitorAvatar}>
        <UserRound size={20} />
      </div>
      <div className={styles.cardBody}>
        <div className={styles.cardHeading}>
          <b>{text('visitor')}</b>
          <i />
          <small>{text('elapsed')}</small>
        </div>
        <p>
          {scene === 3 && beat >= 2800 && beat < 39000
            ? text('unhappy')
            : text('preview')}
        </p>
        <div className={styles.sentiment}>
          {scene === 3 && beat >= 2800 && beat < 39000 ? (
            <Frown size={13} />
          ) : (
            <Smile size={13} />
          )}
          <span>
            <Signal size={12} />
            100%
          </span>
          <small>{text('messageCount')}</small>
        </div>
      </div>
    </div>
  );
  const renderAction = (foreground = false) => (
    <div
      data-course-action
      data-lift-hidden={!foreground && event?.key === 'action'}
      style={{
        visibility:
          !foreground && event?.key === 'action' ? 'hidden' : undefined,
      }}
      className={styles.courseAction}
      data-complete={actionComplete}
    >
      <Zap size={16} />
      <div>
        <small>{text('actionLabel')} · Billing system</small>
        <b>{text('actionName')}</b>
        <span>{text(actionComplete ? 'actionDone' : 'actionLoading')}</span>
      </div>
      {actionComplete && <Check size={16} />}
    </div>
  );
  const renderOtp = (foreground = false) => (
    <div
      data-otp
      style={{
        visibility: !foreground && event?.key === 'otp' ? 'hidden' : undefined,
      }}
      ref={foreground ? undefined : otpSuggestion}
      className={styles.otpSuggestion}
      data-tapped={otpTapped}
    >
      <small>Messages · just now</small>
      <strong>482916</strong>
      <span>Leagen verification code · Tap to autofill</span>
    </div>
  );
  const renderHandback = (foreground = false) => (
    <div
      data-handback
      data-lift-source={!foreground}
      data-lift-hidden={!foreground && event?.key === 'handback'}
      style={{
        visibility:
          !foreground && event?.key === 'handback' ? 'hidden' : undefined,
        opacity: !foreground && event?.key === 'handback' ? 0 : 1,
      }}
      className={styles.note}
    >
      <Check size={14} />
      {text('returned')}
    </div>
  );
  const typed = (key: string, start: number) =>
    text(key).slice(0, Math.max(0, Math.floor((chatBeat - start) / 42)));

  useEffect(() => {
    const node = root.current;
    if (!node) return;
    const copy = node
      .closest('[data-hero-content]')
      ?.querySelector<HTMLElement>('[data-hero-copy]');
    const measure = () => {
      const rect = node.getBoundingClientRect();
      const availableHeight = node.closest('[data-editorial-hero]')
        ? rect.height
        : Math.max(240, window.innerHeight - 180);
      const stackedHero = Boolean(copy) && window.innerWidth < 1024;
      const nextScale = Math.min(
        1,
        rect.width / 660,
        stackedHero ? 1 : Math.max(1, availableHeight) / 820,
      );
      setScale(nextScale);
      setMobileViewport(window.innerWidth < 1024);
      if (copy && nextScale > 0 && window.innerWidth >= 1024) {
        const bounds = copy.getBoundingClientRect();
        const padding = parseFloat(getComputedStyle(copy).paddingBottom) || 0;
        setDesktopTop(
          (bounds.top +
            (bounds.height - padding) / 2 -
            (stage.current?.getBoundingClientRect().top ?? rect.top)) /
            nextScale -
            240,
        );
      } else setDesktopTop(150);
    };
    const resize = new ResizeObserver(measure);
    resize.observe(node);
    if (copy) resize.observe(copy);
    measure();
    window.addEventListener('resize', measure);
    return () => {
      resize.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);
  useEffect(() => {
    const node = root.current;
    if (!node) return;
    if (previewTime !== undefined) {
      setReduced(false);
      return;
    }
    const media = matchMedia('(prefers-reduced-motion: reduce)');
    let visible = false;
    let frame = 0;
    let last = 0;
    let paint = 0;
    const tick = (now: number) => {
      if (last)
        elapsed.current =
          (elapsed.current + Math.min(100, now - last)) % LENGTH;
      last = now;
      if (now - paint > 40) {
        setTime(elapsed.current);
        paint = now;
      }
      frame = requestAnimationFrame(tick);
    };
    const sync = () => {
      cancelAnimationFrame(frame);
      last = 0;
      setReduced(media.matches);
      const active = visible && !document.hidden && !paused && !media.matches;
      setRunning(active);
      if (media.matches) setTime(staticTime);
      else if (active) frame = requestAnimationFrame(tick);
    };
    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = Boolean(entry?.isIntersecting);
        sync();
      },
      { threshold: 0.15 },
    );
    observer.observe(node);
    media.addEventListener('change', sync);
    document.addEventListener('visibilitychange', sync);
    sync();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      media.removeEventListener('change', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, [paused, previewTime]);

  // Screen-space target measurement includes current perspective, float and responsive scale.
  useLayoutEffect(() => {
    const screen = stage.current;
    const anchor = showingAction
      ? screen?.querySelector('[data-course-action]')
      : scene === 1 || scene === 3
        ? screen?.querySelector('[data-live-card]')
        : target.current;
    const box = anchor?.getBoundingClientRect();
    const origin = screen?.getBoundingClientRect();
    const button = (
      otpOffered
        ? otpSuggestion.current
        : lift > 0
          ? liftedPanel.current
          : target.current
    )
      ?.querySelector(
        otpOffered ? 'strong' : ':scope > strong, [class*=sendCircle]',
      )
      ?.getBoundingClientRect();
    if (box && origin) {
      const actualScale = origin.width / 660;
      if (scene === 2 && target.current && screen)
        setInstructionPlane(measurePlane(target.current, screen));
      // Connector terminates at the surface edge, never inside a filled button.
      setPoint({
        x:
          (scene === 0
            ? box.left - origin.left + 3
            : box.left + box.width / 2 - origin.left) / actualScale,
        y: (box.bottom - origin.top + (scene === 2 ? 5 : -5)) / actualScale,
      });
      if (button)
        setCursorPoint({
          x: (button.left + button.width / 2 - origin.left) / actualScale,
          y: (button.top + button.height / 2 - origin.top) / actualScale,
        });
    }
  }, [time, scale, scene, showingAction, otpOffered, lift]);

  useLayoutEffect(() => {
    if (inputText.current)
      inputText.current.scrollLeft = inputText.current.scrollWidth;
    const list = messageList.current;
    if (!list) return;
    list.scrollTop = list.scrollHeight;
    const viewport = list.getBoundingClientRect();
    // Older messages remain in the transcript, but never leave a chopped fragment.
    for (const child of Array.from(list.children)) {
      const item = child as HTMLElement;
      const bounds = item.getBoundingClientRect();
      item.style.visibility =
        bounds.top < viewport.top - 1 ? 'hidden' : 'visible';
    }
  }, [time]);

  const entranceProgress = clamp(
    (time - (entrance === 'cliff' ? 250 : 0)) /
      (entrance === 'cliff' ? 2200 : 650),
  );
  const enter = reduced
    ? 1
    : entrance === 'cliff'
      ? 1 - Math.pow(1 - entranceProgress, 3)
      : entranceProgress;
  const arrivalTime =
    reduced || (arrived && previewTime === undefined) ? 4200 : time;
  const smooth = (n: number) => n * n * (3 - 2 * n);
  const rise = smooth(clamp((arrivalTime - 250) / 1550));
  const approach = smooth(clamp((arrivalTime - 1800) / 1700));
  const settle = clamp((arrivalTime - 3500) / 650);
  const arrivalY =
    arrivalTime < 1800
      ? 1100 - 1440 * rise
      : -340 * (1 - approach) - Math.sin(settle * Math.PI) * (1 - settle) * 14;
  const arrivalScale = 0.56 + 0.44 * approach;
  const inFront = arrivalTime >= 1800;
  const spinProgress = clamp((time - 47200) / 2800);
  const spin = reduced
    ? 0
    : 360 * (spinProgress * spinProgress * (3 - 2 * spinProgress));
  const turn = (reduced ? -16 : -18 + Math.sin(time / 4200) * 3) + spin;
  const faceAngle = ((turn % 360) + 360) % 360;
  const showingBack = !reduced && faceAngle > 90 && faceAngle < 270;
  const float = reduced
    ? 0
    : Math.sin(time / 1900) *
      5 *
      (entrance === 'cliff' ? smooth(clamp((arrivalTime - 3500) / 700)) : 1);
  const gestureBeat =
    scene === 0
      ? beat - (beat < 8000 ? 3800 : beat < 16000 ? 11800 : 18800)
      : chatBeat;
  const click = !reduced && gestureBeat >= 3900 && gestureBeat < 4200;
  const showCursor =
    !reduced &&
    scene < 3 &&
    scene !== 1 &&
    gestureBeat > 3300 &&
    gestureBeat < 4200;
  const travel = clamp((gestureBeat - 3400) / 450);
  // Notes have their own bounded dwell and never follow a moving pop-out.
  const noteWindows = [
    [5300, 10300],
    [20500, 25500],
    [34200, 39200],
    [51200, 56200],
    [65500, 70000],
    [81500, 86500],
    [97500, 102000],
    [103500, 108500],
    [111500, 116500],
    [119500, 124500],
    [127500, 132500],
    [134500, 139500],
  ];
  const noteWindow = noteWindows.find(
    ([start, end]) => time >= start! && time < end!,
  );
  const noteKey = noteWindow?.[0] ?? -1;
  const moving =
    (event &&
      !['customer', 'manager'].includes(event.key) &&
      eventLift < 0.999) ||
    (scene === 2 && lift > 0 && lift < 0.999);
  const hasCallout = !showingBack && !moving && !!noteWindow;
  const noteOpacity = noteWindow
    ? Math.min(
        clamp((time - noteWindow[0]!) / 300),
        clamp((noteWindow[1]! - time) / 300),
      )
    : 0;
  const compact = mobileViewport && scale < 0.72;
  const attentionNote = scene === 3 && !event && time < 126000;
  const explanationRef = useRef<HTMLDivElement>(null);
  const placedNote = useRef('');
  const [annotation, setAnnotation] = useState({
    left: 0,
    top: 0,
    arrow: 'down',
    tip: 50,
  });
  useLayoutEffect(() => {
    const host = root.current;
    const bubble = explanationRef.current;
    const canvas = stage.current;
    if (!host || !bubble || !canvas || !hasCallout) return;
    const placementKey = `${noteKey}:${scale}:${compact}`;
    if (placedNote.current === placementKey) return;
    const lifted = canvas.querySelector<HTMLElement>(
      `[class*="${scene === 2 && lift > 0 ? 'liftedInstruction' : 'eventPopout'}"]`,
    );
    const target =
      lifted ??
      canvas.querySelector<HTMLElement>(
        (event?.key === 'customer'
          ? '[data-chat-intro]'
          : event?.key === 'manager'
            ? '[data-monitor-title]'
            : event?.selector) ??
          (scene === 2
            ? '[data-handback]'
            : showingAction
              ? '[data-course-action]'
              : scene === 3
                ? '[data-live-card]'
                : '[data-audience]'),
      );
    if (!target) return;
    const h = host.getBoundingClientRect();
    const t = target.getBoundingClientRect();
    const w = bubble.offsetWidth;
    const bh = bubble.offsetHeight;
    const gap = 12;
    const x = t.left - h.left,
      y = t.top - h.top;
    const cx = x + t.width / 2,
      cy = y + t.height / 2;
    const choices = [
      { left: x - w - gap, top: cy - bh / 2, arrow: 'right' },
      { left: x + t.width + gap, top: cy - bh / 2, arrow: 'left' },
      { left: cx - w / 2, top: y - bh - gap, arrow: 'down' },
      { left: cx - w / 2, top: y + t.height + gap, arrow: 'up' },
    ];
    const limitY = h.height - 8;
    const badges = Array.from(
      canvas.querySelectorAll<HTMLElement>('[data-audience]'),
    )
      .filter((e) => e !== target)
      .map((e) => e.getBoundingClientRect());
    const ranked = choices
      .map((c, index) => {
        const left = Math.max(8, Math.min(c.left, h.width - w - 8));
        const top = Math.max(8, Math.min(c.top, limitY - bh));
        const overlap =
          Math.max(0, Math.min(left + w, x + t.width) - Math.max(left, x)) *
          Math.max(0, Math.min(top + bh, y + t.height) - Math.max(top, y));
        const badgeOverlap = badges.some(
          (b) =>
            left + w > b.left - h.left &&
            left < b.right - h.left &&
            top + bh > b.top - h.top &&
            top < b.bottom - h.top,
        );
        return {
          ...c,
          left,
          top,
          score:
            overlap * 100 +
            (badgeOverlap ? 100000 : 0) +
            Math.abs(left - c.left) +
            Math.abs(top - c.top) +
            index,
        };
      })
      .sort((a, b) => a.score - b.score);
    placedNote.current = placementKey;
    // Explain the settled attention card from above, never across its face.
    const best = attentionNote
      ? {
          left: Math.max(8, Math.min(cx - w / 2, h.width - w - 8)),
          top: Math.max(8, y - bh - gap),
          arrow: 'down',
        }
      : ranked[0]!;
    const horizontal = best.arrow === 'up' || best.arrow === 'down';
    const tip = Math.max(
      12,
      Math.min(
        88,
        horizontal
          ? ((cx - best.left) / w) * 100
          : ((cy - best.top) / bh) * 100,
      ),
    );
    setAnnotation((old) =>
      Math.abs(old.left - best.left) < 1 &&
      Math.abs(old.top - best.top) < 1 &&
      old.arrow === best.arrow &&
      Math.abs(old.tip - tip) < 1
        ? old
        : { left: best.left, top: best.top, arrow: best.arrow, tip },
    );
  }, [
    noteKey,
    attentionNote,
    lift,
    event?.key,
    scale,
    compact,
    hasCallout,
    scene,
    event?.selector,
    showingAction,
  ]);
  const explanationTitle =
    event?.key === 'otp'
      ? 'Verify securely'
      : event?.key === 'action' || showingAction
        ? 'Connected actions'
        : event?.key === 'sentiment' || explainingEscalation
          ? 'Your team can step in'
          : event?.key === 'handback'
            ? 'Back to Sarah'
            : event?.key === 'customer'
              ? 'The customer’s view'
              : event?.key === 'manager'
                ? 'Your monitoring panel'
                : scene === 2
                  ? 'Guide Sarah privately'
                  : text(`c${scene}`);
  const explanationBody =
    event?.key === 'otp'
      ? 'A one-time code confirms the customer’s identity.'
      : event?.key === 'action' || showingAction
        ? 'Actions securely retrieve information from connected systems.'
        : event?.key === 'sentiment' || explainingEscalation
          ? 'Sentiment is one escalation signal. Your rules notify the team when help is needed.'
          : event?.key === 'handback'
            ? 'The issue is fixed. Sarah resumes with the full context.'
            : event?.key === 'customer'
              ? 'Customers get help directly from their phone.'
              : event?.key === 'manager'
                ? 'Follow conversations and step in when needed.'
                : scene === 2
                  ? 'Give the agent context while it keeps helping.'
                  : text(`b${scene}`);
  const deviceStyle = {
    '--enter': enter,
    '--desktop-top': `${desktopTop}px`,
    '--phone-top': `${Math.max(5, desktopTop - 150)}px`,
    '--arrival-y': `${arrivalY}px`,
    '--arrival-scale': arrivalScale,
    '--landed': approach,
    '--turn': `${turn}deg`,
    '--float': `${float}px`,
  } as CSSProperties;
  return (
    <div
      data-website-demo
      data-entrance={entrance}
      data-in-front={inFront}
      ref={root}
      className={styles.root}
      data-running={running}
      data-compact-notes={compact}
    >
      <span className="sr-only">{text('summary')}</span>
      {hasCallout && (
        <div
          ref={explanationRef}
          className={compact ? styles.compactExplanation : styles.callout}
          data-note-arrow={annotation.arrow}
          style={
            {
              left: annotation.left,
              top: annotation.top,
              transform: 'none',
              '--note-tip': `${annotation.tip}%`,
              opacity: noteOpacity,
            } as CSSProperties
          }
        >
          <small>0{scene + 1} / LEAGEN</small>
          <b>{explanationTitle}</b>
          <p>{explanationBody}</p>
        </div>
      )}
      <div
        className={styles.slot}
        style={{
          width: 660 * scale,
          height: 820 * scale,
          marginInline: 'auto',
        }}
      >
        <div
          ref={stage}
          className={styles.stage}
          style={{ transform: `scale(${scale})` }}
          aria-hidden="true"
        >
          <div className={styles.halo} />
          <div
            className={styles.device}
            style={deviceStyle}
            data-desktop={desktop}
            data-scene={scene}
            data-back={showingBack}
            data-focus={focusCard}
          >
            <div className={styles.back}>
              <div className={styles.engraving} />
            </div>
            <div className={styles.screen}>
              {!desktop && (
                <>
                  <div className={styles.camera} />
                  <div className={styles.status}>
                    9:41 <span>••• ▰</span>
                  </div>
                </>
              )}
              <header className={styles.header}>
                {desktop ? (
                  <>
                    <AppLogo className="w-24" />
                    <span>
                      <Monitor size={14} />
                      {text('monitor')}
                    </span>
                  </>
                ) : (
                  <>
                    <Image
                      unoptimized
                      src="/images/agents/sarah-avatar.jpg"
                      alt=""
                      width={36}
                      height={36}
                    />
                    <b className={styles.headerName}>
                      Sarah <small>Sales Assistant</small>
                    </b>
                    <Volume2 size={15} />
                    <RotateCcw size={15} />
                    <X size={15} />
                  </>
                )}
              </header>
              <div className={styles.workspace}>
                {desktop && (
                  <aside className={styles.sidebar} data-attention={escalated}>
                    <span>
                      {text('attention')} <b>{escalated ? '1' : '0'}</b>
                    </span>
                    <span>
                      {text('live')} <b>{escalated ? '0' : '1'}</b>
                    </span>
                    {renderSentiment()}
                    <span>
                      {text('idle')} <b>0</b>
                    </span>
                  </aside>
                )}
                <div className={styles.conversation}>
                  {desktop && (
                    <div
                      data-monitor-title
                      className={styles.conversationTitle}
                    >
                      <span>{text('visitor')}</span>
                      <span>{text('chat')}</span>
                      <b>{text('agent')}</b>
                      <Image
                        unoptimized
                        src="/images/agents/sarah-avatar.jpg"
                        alt=""
                        width={25}
                        height={25}
                      />
                    </div>
                  )}
                  <div ref={messageList} className={styles.messages}>
                    {scene === 0 && beat < 1800 && <Thinking />}
                    {(scene > 0 || beat >= 1800) && (
                      <AgentMessage name={text('agent')} role={text('role')}>
                        {text('greeting')}
                      </AgentMessage>
                    )}
                    {(scene > 0 || beat >= 8000) && (
                      <div className={styles.user}>{text('question')}</div>
                    )}
                    {scene === 0 && beat >= 8000 && beat < 9400 && <Thinking />}
                    {(scene > 0 || beat >= 9400) && (
                      <AgentMessage name={text('agent')} role={text('role')}>
                        {text('askIdentity')}
                      </AgentMessage>
                    )}
                    {(scene > 0 || beat >= 16000) && (
                      <div className={styles.user}>{text('identityReply')}</div>
                    )}
                    {scene === 0 && beat >= 16000 && beat < 17400 && (
                      <Thinking />
                    )}
                    {(scene > 0 || beat >= 17400) && (
                      <AgentMessage name={text('agent')} role={text('role')}>
                        {text('otpPrompt')}
                      </AgentMessage>
                    )}
                    {(scene > 0 || beat >= 29000) && (
                      <div className={styles.user}>{text('otp')}</div>
                    )}
                    {scene === 0 && beat >= 29000 && beat < 30500 && (
                      <Thinking />
                    )}
                    {(scene > 0 || beat >= 30500) && (
                      <AgentMessage name={text('agent')} role={text('role')}>
                        {text('verified')}
                      </AgentMessage>
                    )}
                    {(showingAction || scene > 0) && renderAction()}
                    {showingAction && !actionComplete && <Thinking />}
                    {(showingAction || scene > 0) && actionComplete && (
                      <AgentMessage name={text('agent')} role={text('role')}>
                        <div>{text('datesIntro')}</div>
                        <div className={styles.dateList}>
                          {text('datesList')}
                        </div>
                        <div>{text('datesPrompt')}</div>
                      </AgentMessage>
                    )}
                    {scene === 3 && (
                      <div className={styles.user}>{text('followup')}</div>
                    )}
                    {scene === 2 && beat > 4700 && (
                      <>
                        <div data-instruction-result className={styles.note}>
                          <LockKeyhole size={12} />
                          {text('sent')}
                        </div>
                        {beat < 13900 ? (
                          <Thinking />
                        ) : (
                          <AgentMessage
                            name={text('agent')}
                            role={text('role')}
                          >
                            {text('guided')}
                          </AgentMessage>
                        )}
                      </>
                    )}
                    {escalated && (
                      <div className={styles.escalation}>
                        <Bell size={16} />
                        <div>
                          <b>{text('notified')}</b>
                          <small>{text('reason')}</small>
                        </div>
                      </div>
                    )}
                    {scene === 3 && beat > 23000 && (
                      <>
                        <div className={styles.note}>
                          Jordan from the billing group has taken over
                        </div>
                        <AgentMessage name="Jordan" role="Billing team">
                          Hi Alex, I’m Jordan from billing. I’ll check the
                          duplicate charge for you now.
                        </AgentMessage>
                      </>
                    )}
                    {scene === 3 && beat > 31000 && (
                      <AgentMessage name="Jordan" role="Billing team">
                        You’re right — the same service was charged twice. I’ve
                        removed the duplicate $120 charge. Your corrected
                        invoice is $120. I’m sorry about that.
                      </AgentMessage>
                    )}
                    {scene === 3 && beat > 39000 && (
                      <div className={styles.user}>
                        Thanks, Jordan. That explains it — I’m happy to pay the
                        corrected invoice.
                      </div>
                    )}
                    {scene === 3 && beat > 46000 && renderHandback()}
                    {scene === 3 && beat > 54000 && (
                      <AgentMessage name="Sarah" role="Sales Assistant">
                        Thanks, Alex. Your corrected invoice is $120. Here is
                        your secure payment link. Is there anything else I can
                        help with?
                      </AgentMessage>
                    )}
                  </div>
                  {otpOffered && renderOtp()}
                  {(scene === 0 ||
                    scene === 2 ||
                    (scene === 3 && beat > 45000 && beat < 46000)) && (
                    <div
                      ref={target}
                      className={styles.composer}
                      data-click={click}
                      style={
                        scene === 2 && lift > 0
                          ? { visibility: 'hidden' }
                          : undefined
                      }
                    >
                      {scene === 2 ? (
                        <InstructionPanel
                          value={
                            beat < 4300
                              ? typed('instruction', 800)
                              : text('instruction')
                          }
                          sent={beat > 4300}
                        />
                      ) : scene === 3 ? (
                        <strong>
                          {text('handback')} <Send size={14} />
                        </strong>
                      ) : (
                        <>
                          <span ref={inputText} className={styles.inputText}>
                            {beat >= 4500 && beat < 8000
                              ? typed('question', 4500)
                              : beat >= 12000 && beat < 16000
                                ? typed('identityReply', 12000)
                                : beat >= 27500 && beat < 29000
                                  ? text('otp')
                                  : 'Message…'}
                          </span>
                          <span className={styles.sendCircle}>
                            <ArrowUp size={18} />
                          </span>
                        </>
                      )}
                    </div>
                  )}
                  {desktop && (
                    <div className={styles.toolbar}>
                      <span>
                        <Hand size={12} />
                        Take Over
                      </span>
                      <span data-active={scene === 2}>
                        <MessageSquare size={12} />
                        Instruct Agent
                      </span>
                      <span>
                        <Bot size={12} />
                        Agent Note
                      </span>
                      <span>
                        <FileText size={12} />
                        Private Note
                      </span>
                      <span className={styles.escalateControl}>
                        <TriangleAlert size={12} />
                        Escalate
                      </span>
                      <span className={styles.resolveControl}>
                        <CircleCheck size={12} />
                        Resolve
                      </span>
                    </div>
                  )}
                  {scene === 2 && (
                    <div className={styles.mode}>
                      <LockKeyhole size={12} />
                      {text('instruct')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {(showCursor || otpTapped) && (
            <div
              className={styles.cursor}
              style={{
                left: cursorPoint.x + 50 * (1 - travel) - 10,
                top: cursorPoint.y + 38 * (1 - travel) - 10,
              }}
              data-click={click || otpTapped}
            >
              <span className={styles.touchDot} />
            </div>
          )}
          {!showingBack && (desktop || time >= 4200) && (
            <div
              data-audience
              className={styles.perspectiveLabel}
              style={{
                top: desktop
                  ? desktopTop + 490
                  : Math.max(5, desktopTop - 150) + 785,
              }}
              key={desktop ? 'team' : 'customer'}
            >
              {desktop ? 'Team conversation manager' : 'Customer experience'}
            </div>
          )}
          {event && !['customer', 'manager'].includes(event.key) && (
            <div
              className={styles.eventPopout}
              data-event={event.key}
              style={{
                left: 0,
                top: 0,
                width: eventOrigin.width,
                height: eventOrigin.height,
                transform: cornerTransform(
                  eventOrigin.width,
                  eventOrigin.height,
                  liftedCorners,
                ),
              }}
            >
              {event.key === 'otp' && renderOtp(true)}
              {event.key === 'action' && renderAction(true)}
              {event.key === 'sentiment' && renderSentiment(true)}
              {event.key === 'handback' && renderHandback(true)}
              {['customer', 'manager'].includes(event.key) && (
                <div className={styles.perspectiveLabel}>{event.title}</div>
              )}
            </div>
          )}
          {scene === 2 && lift > 0 && (
            <div
              ref={liftedPanel}
              className={styles.liftedInstruction}
              style={{
                left: 0,
                top: 0,
                width: instructionPlane.width,
                height: instructionPlane.height,
                transform: cornerTransform(
                  instructionPlane.width,
                  instructionPlane.height,
                  instructionPlane.corners.map((point) => {
                    // Apply one uniform zoom to the entire source plane. Do not
                    // independently interpolate corners into a flat rectangle:
                    // that changes the text's aspect ratio during the journey.
                    const zoom =
                      1 +
                      (Math.min(1.6, 590 / instructionPlane.width) - 1) * lift;
                    const origin = instructionPlane.corners[0]!;
                    return {
                      x:
                        origin.x +
                        (35 - origin.x) * lift +
                        (point.x - origin.x) * zoom,
                      y:
                        origin.y +
                        (desktopTop + 130 - origin.y) * lift +
                        (point.y - origin.y) * zoom,
                    };
                  }),
                ),
              }}
            >
              <InstructionPanel
                value={
                  beat < 4300 ? typed('instruction', 800) : text('instruction')
                }
                sent={beat > 4300}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AgentMessage({
  children,
  name,
  role,
}: {
  children: ReactNode;
  name: string;
  role: string;
}) {
  return (
    <div data-chat-intro className={styles.agent}>
      <div className={styles.agentIdentity}>
        <b>{name}</b>
        <span>{role}</span>
      </div>
      <div className={styles.agentBubble}>
        {name === 'Jordan' ? (
          <span
            className={styles.messageAvatar}
            aria-label="Jordan, billing team"
          >
            <UserRound size={24} />
          </span>
        ) : (
          <Image
            className={styles.messageAvatar}
            unoptimized
            src="/images/agents/sarah-avatar.jpg"
            alt=""
            width={28}
            height={28}
          />
        )}
        {children}
      </div>
      <div className={styles.messageFeedback}>
        <span>Just now</span>
        <ThumbsUp size={13} aria-label="Thumbs up" />
        <ThumbsDown size={13} aria-label="Thumbs down" />
      </div>
    </div>
  );
}

function Thinking() {
  return (
    <div className={styles.thinking}>
      <Image
        unoptimized
        src="/images/agents/sarah-avatar.jpg"
        alt=""
        width={28}
        height={28}
      />
      <span>
        <i />
        <i />
        <i />
      </span>
    </div>
  );
}

function InstructionPanel({ value, sent }: { value: string; sent: boolean }) {
  return (
    <>
      <small>
        <MessageSquare size={14} />
        Instruct agent — the AI will act on this immediately
      </small>
      <div className={styles.instructionInput}>
        {value || 'Tell the agent what to do…'}
      </div>
      <strong>{sent ? <Check size={18} /> : <Send size={18} />}</strong>
    </>
  );
}

// Homography maps the untransformed source rectangle onto its four measured corners.
function cornerTransform(
  width: number,
  height: number,
  points: { x: number; y: number }[],
) {
  const matrix: number[][] = [];
  [
    [0, 0],
    [width, 0],
    [width, height],
    [0, height],
  ].forEach(([x = 0, y = 0], i) => {
    const { x: u, y: v } = points[i]!;
    matrix.push(
      [x, y, 1, 0, 0, 0, -u * x, -u * y, u],
      [0, 0, 0, x, y, 1, -v * x, -v * y, v],
    );
  });
  for (let i = 0; i < 8; i++) {
    let pivot = i;
    for (let j = i + 1; j < 8; j++)
      if (Math.abs(matrix[j]![i]!) > Math.abs(matrix[pivot]![i]!)) pivot = j;
    [matrix[i], matrix[pivot]] = [matrix[pivot]!, matrix[i]!];
    const divisor = matrix[i]![i]!;
    if (Math.abs(divisor) < 1e-8) return 'none';
    for (let k = i; k < 9; k++) matrix[i]![k] = matrix[i]![k]! / divisor;
    for (let j = 0; j < 8; j++)
      if (j !== i) {
        const factor = matrix[j]![i]!;
        for (let k = i; k < 9; k++)
          matrix[j]![k] = matrix[j]![k]! - factor * matrix[i]![k]!;
      }
  }
  const [a, b, c, d, e, f, g, h] = matrix.map((row) => row[8]!);
  return `matrix3d(${a},${d},0,${g},${b},${e},0,${h},0,0,1,0,${c},${f},0,1)`;
}

function measurePlane(source: HTMLElement, canvas: HTMLElement) {
  const bounds = canvas.getBoundingClientRect();
  const ratio = bounds.width / 660;
  const css = getComputedStyle(source);
  const previousPosition = source.style.position;
  if (css.position === 'static') source.style.position = 'relative';
  const corners = [
    [0, 0],
    [1, 0],
    [1, 1],
    [0, 1],
  ].map(([right, bottom]) => {
    const probe = document.createElement('i');
    probe.style.cssText = `position:absolute;pointer-events:none;width:0;height:0;${right ? 'right' : 'left'}:-${parseFloat(right ? css.borderRightWidth : css.borderLeftWidth)}px;${bottom ? 'bottom' : 'top'}:-${parseFloat(bottom ? css.borderBottomWidth : css.borderTopWidth)}px;`;
    source.appendChild(probe);
    const rect = probe.getBoundingClientRect();
    probe.remove();
    return {
      x: (rect.left - bounds.left) / ratio,
      y: (rect.top - bounds.top) / ratio,
    };
  });
  source.style.position = previousPosition;
  return { width: source.offsetWidth, height: source.offsetHeight, corners };
}
